import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
  type MutableRefObject,
} from "react";
import { useHistory } from "react-router-dom";

import { useGlobalSearchContext } from "src/components/Search/GlobalSearchContext";
import { EVENT_TRACKING } from "src/consts/eventTracking";
import PATHS, { getSearchPath, PERSON_TAB, url } from "src/consts/urlPaths";
import { filterTypes } from "src/pages/SearchPage/SearchPage.config";
import { trackExploreJourneyEvent } from "src/services/exploreOrganisationAnalytics";
import {
  personDetailsPath,
  positionDetailsPath,
  teamDetailsPath,
} from "src/util/routing";

import {
  HeaderSearchRecentsContext,
  headerSearchOptionId,
  type HeaderSearchRecentsApi,
  type HeaderSearchTarget,
} from "./HeaderSearchRecentsContext";
import { useHeaderSearchRecents } from "./useHeaderSearchRecents";

const searchFilterForKind = (kind: user_prefs.RecentSearchKind) => {
  switch (kind) {
    case "person":
      return filterTypes.PEOPLE;
    case "team":
      return filterTypes.TEAMS;
    case "tag":
    case "role":
      return filterTypes.TAGS;
    case "position":
      return filterTypes.POSITIONS;
    case "query":
      return filterTypes.ALL;
    default: {
      const unexpected: never = kind;
      return unexpected;
    }
  }
};

const trackOpened = (
  resultType: string,
  resultSource: "recent_search" | "recently_viewed" | "open_allocations"
) => {
  trackExploreJourneyEvent(EVENT_TRACKING.SEARCH_RESULT_COMPLETED, {
    lifecycle_stage: "completed",
    entry_point: "global_search",
    operation_intent: "search",
    result_type: resultType,
    result_source: resultSource,
  });
};

type HeaderSearchRecentsBridgeProps = {
  apiRef: MutableRefObject<HeaderSearchRecentsApi | null>;
  menuOpen: boolean;
  /** How many recent searches and how many recently viewed rows to show. */
  visibleLimit: number;
  children: ReactNode;
};

const HeaderSearchRecentsBridge = ({
  apiRef,
  menuOpen,
  visibleLimit,
  children,
}: HeaderSearchRecentsBridgeProps) => {
  const history = useHistory();
  const { setSearchTerm } = useGlobalSearchContext();
  const {
    loading,
    recentSearches,
    recentlyViewedPeople,
    recentlyViewed,
    clearSearches,
    rememberSearch,
    rememberPerson,
    rememberOpenedPerson,
    rememberViewed,
    rememberOpenedView,
  } = useHeaderSearchRecents();
  const [activeIndex, setActiveIndex] = useState(-1);
  const resetHighlight = useCallback(() => {
    setActiveIndex(-1);
  }, []);

  const visibleSearches = useMemo(
    () => recentSearches.slice(0, visibleLimit),
    [recentSearches, visibleLimit]
  );
  const visibleViewed = useMemo(
    () => recentlyViewed.slice(0, visibleLimit),
    [recentlyViewed, visibleLimit]
  );

  const targets = useMemo<readonly HeaderSearchTarget[]>(
    () => [
      ...visibleSearches.map((search) => ({
        type: "search" as const,
        search,
      })),
      ...visibleViewed.map((item) => ({ type: "viewed" as const, item })),
    ],
    [visibleSearches, visibleViewed]
  );

  const clampedIndex =
    targets.length === 0
      ? -1
      : Math.min(Math.max(activeIndex, -1), targets.length - 1);

  const openTarget = useCallback(
    (
      target: HeaderSearchTarget,
      destination: "profile" | "allocations" = "profile"
    ) => {
      switch (target.type) {
        case "search": {
          trackOpened(target.search.kind, "recent_search");
          void rememberSearch(target.search);
          setSearchTerm(target.search.query);
          history.push(
            getSearchPath(
              searchFilterForKind(target.search.kind),
              target.search.query,
              false
            )
          );
          return;
        }
        case "person": {
          const allocations = destination === "allocations";
          trackOpened(
            "person",
            allocations ? "open_allocations" : "recently_viewed"
          );
          void rememberPerson(target.person);
          history.push(
            personDetailsPath(
              { aggregateId: target.person.id },
              allocations ? PERSON_TAB.ALLOCATIONS : undefined
            )
          );
          return;
        }
        case "viewed": {
          const { item } = target;
          const allocations =
            destination === "allocations" && item.kind === "person";
          trackOpened(
            item.kind,
            allocations ? "open_allocations" : "recently_viewed"
          );
          void rememberViewed(item);
          switch (item.kind) {
            case "person":
              history.push(
                personDetailsPath(
                  { aggregateId: item.id },
                  allocations ? PERSON_TAB.ALLOCATIONS : undefined
                )
              );
              return;
            case "team": {
              const path = teamDetailsPath(item.id);
              if (path) {
                history.push(path);
              }
              return;
            }
            case "tag":
              history.push(url(PATHS.TAG_DETAILS, { id: item.id }));
              return;
            case "position":
              history.push(positionDetailsPath(item.id));
              return;
            default: {
              const unexpected: never = item.kind;
              return unexpected;
            }
          }
        }
        default: {
          const unexpected: never = target;
          return unexpected;
        }
      }
    },
    [history, rememberPerson, rememberSearch, rememberViewed, setSearchTerm]
  );

  const onInputKeyDown = useCallback(
    (event: {
      key: string;
      preventDefault: () => void;
      stopPropagation: () => void;
    }) => {
      if (!menuOpen || targets.length === 0) {
        return false;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        event.stopPropagation();
        setActiveIndex(
          clampedIndex < 0 ? 0 : Math.min(clampedIndex + 1, targets.length - 1)
        );
        return true;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        event.stopPropagation();
        setActiveIndex(
          clampedIndex < 0 ? targets.length - 1 : Math.max(clampedIndex - 1, 0)
        );
        return true;
      }
      if (event.key === "Enter") {
        const target = targets[clampedIndex];
        if (!target) {
          return false;
        }
        event.preventDefault();
        event.stopPropagation();
        openTarget(target);
        return true;
      }
      return false;
    },
    [clampedIndex, menuOpen, openTarget, targets]
  );

  const api = useMemo<HeaderSearchRecentsApi>(
    () => ({
      loading,
      recentSearches: visibleSearches,
      recentlyViewedPeople,
      recentlyViewed: visibleViewed,
      clearSearches,
      resetHighlight,
      rememberSearch,
      rememberPerson,
      rememberOpenedPerson,
      rememberViewed,
      rememberOpenedView,
      targets,
      activeIndex: clampedIndex,
      setActiveIndex,
      activeOptionId:
        clampedIndex >= 0 ? headerSearchOptionId(clampedIndex) : undefined,
      onInputKeyDown,
      openTarget,
    }),
    [
      clampedIndex,
      clearSearches,
      loading,
      onInputKeyDown,
      openTarget,
      recentlyViewedPeople,
      visibleSearches,
      visibleViewed,
      rememberOpenedPerson,
      rememberOpenedView,
      rememberPerson,
      rememberSearch,
      rememberViewed,
      resetHighlight,
      targets,
    ]
  );

  useEffect(() => {
    apiRef.current = api;
    return () => {
      apiRef.current = null;
    };
  }, [api, apiRef]);

  return (
    <HeaderSearchRecentsContext.Provider value={api}>
      {children}
    </HeaderSearchRecentsContext.Provider>
  );
};

export default HeaderSearchRecentsBridge;
